/**
 * ==========================================================
 * CHEMARRY PRODUCT v1.2
 * Página de producto personalizada
 * Compatible con Shopify Dawn 15.4.1
 * ==========================================================
 */

(() => {
  'use strict';

  class ChemarryProduct {
    constructor(container) {
      this.container = container;

      this.productJSON = this.readProductJSON();

      this.variantSelector = container.querySelector(
        '[data-product-variants]'
      );

      this.quantityInput = container.querySelector(
        '[data-product-quantity-input]'
      );

      this.addButton = container.querySelector(
        '[data-product-add]'
      );

      this.price = container.querySelector(
        '[data-product-price]'
      );

      this.comparePrice = container.querySelector(
        '[data-product-compare-price]'
      );

      this.availability = container.querySelector(
        '[data-product-availability]'
      );

      this.sku = container.querySelector(
        '[data-product-sku]'
      );

      this.mainImage = container.querySelector(
        '[data-product-main-image]'
      );

      this.imageLink = container.querySelector(
        '[data-product-image-link]'
      );

      this.thumbs = [
        ...container.querySelectorAll(
          '[data-product-thumb]'
        )
      ];

      this.mobilePrice = container.querySelector(
        '[data-mobile-price]'
      );

      this.mobileButton = container.querySelector(
        '[data-mobile-add]'
      );

      this.stickyBar = container.querySelector(
        '[data-mobile-sticky]'
      );

      this.toastTimeout = null;
      this.stickyObserver = null;
      this.isSubmitting = false;

      this.variant = this.getCurrentVariant();

      this.bindEvents();
      this.bindOptionInputs();
      this.bindQuantityInput();
      this.updateQuantityButtons();
      this.updateVariant(this.variant, false);
      this.initStickyBar();
    }

    /* ======================================================
       DATOS DEL PRODUCTO
       ====================================================== */

    readProductJSON() {
      const jsonElement = this.container.querySelector(
        '[data-product-json]'
      );

      if (!jsonElement) {
        return null;
      }

      try {
        return JSON.parse(jsonElement.textContent);
      } catch (error) {
        console.error(
          'Chemarry Product: JSON del producto inválido.',
          error
        );

        return null;
      }
    }

    getCurrentVariant() {
      if (
        !this.productJSON ||
        !Array.isArray(this.productJSON.variants)
      ) {
        return null;
      }

      const selectedId = Number(
        this.variantSelector?.value ||
          this.productJSON.selected_variant_id
      );

      return (
        this.productJSON.variants.find(
          (variant) => Number(variant.id) === selectedId
        ) ||
        this.productJSON.variants[0] ||
        null
      );
    }

    /* ======================================================
       EVENTOS
       ====================================================== */

    bindEvents() {
      this.variantSelector?.addEventListener(
        'change',
        () => this.onVariantChange()
      );

      this.container
        .querySelectorAll('[data-quantity-minus]')
        .forEach((button) => {
          button.addEventListener('click', () => {
            this.changeQuantity(-1);
          });
        });

      this.container
        .querySelectorAll('[data-quantity-plus]')
        .forEach((button) => {
          button.addEventListener('click', () => {
            this.changeQuantity(1);
          });
        });

      this.addButton?.addEventListener(
        'click',
        (event) => this.addToCart(event)
      );

      this.mobileButton?.addEventListener(
        'click',
        (event) => this.addToCart(event)
      );

      this.thumbs.forEach((button) => {
        button.addEventListener('click', () => {
          this.changeImage(button);
        });
      });
    }

    bindQuantityInput() {
      if (!this.quantityInput) {
        return;
      }

      this.quantityInput.addEventListener(
        'input',
        () => {
          this.normalizeQuantity();
          this.updateQuantityButtons();
        }
      );

      this.quantityInput.addEventListener(
        'change',
        () => {
          this.normalizeQuantity();
          this.updateQuantityButtons();
        }
      );
    }

    bindOptionInputs() {
      const optionInputs = [
        ...this.container.querySelectorAll(
          '[data-product-option]'
        )
      ];

      optionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.handleOptionSelection();
        });
      });

      this.updateSelectedOptionLabels();
    }

    /* ======================================================
       VARIANTES
       ====================================================== */

    onVariantChange() {
      const variant = this.getCurrentVariant();

      if (!variant) {
        return;
      }

      this.updateVariant(variant, true);
    }

    updateVariant(variant, updateURL = true) {
      if (!variant) {
        this.variant = null;
        this.updateUnavailableState();
        return;
      }

      this.variant = variant;

      if (this.variantSelector) {
        this.variantSelector.value = String(variant.id);
      }

      this.updatePrice();
      this.updateAvailability();
      this.updateSKU();
      this.updateImage();
      this.updateButtons();
      this.updateQuantityRule();

      if (updateURL) {
        this.updateBrowserURL(variant.id);
      }
    }

    handleOptionSelection() {
      if (
        !this.productJSON ||
        !Array.isArray(this.productJSON.variants)
      ) {
        return;
      }

      const selectedOptions = this.getSelectedOptions();

      const matchingVariant =
        this.productJSON.variants.find((variant) => {
          const variantOptions = Array.isArray(
            variant.options
          )
            ? variant.options
            : [];

          if (
            variantOptions.length !==
            selectedOptions.length
          ) {
            return false;
          }

          return variantOptions.every(
            (option, index) =>
              String(option) ===
              String(selectedOptions[index])
          );
        });

      this.updateSelectedOptionLabels();

      if (!matchingVariant) {
        this.variant = null;
        this.updateUnavailableState();
        return;
      }

      if (this.variantSelector) {
        this.variantSelector.value = String(
          matchingVariant.id
        );
      }

      this.updateVariant(matchingVariant, true);
    }

    getSelectedOptions() {
      const groups = [
        ...this.container.querySelectorAll(
          '[data-product-option-group]'
        )
      ];

      return groups
        .map((group) => {
          const checkedInput = group.querySelector(
            '[data-product-option]:checked'
          );

          const select = group.querySelector(
            'select[data-product-option]'
          );

          if (checkedInput) {
            return checkedInput.value;
          }

          if (select) {
            return select.value;
          }

          return null;
        })
        .filter((value) => value !== null);
    }

    updateSelectedOptionLabels() {
      const groups = [
        ...this.container.querySelectorAll(
          '[data-product-option-group]'
        )
      ];

      groups.forEach((group) => {
        const label = group.querySelector(
          '[data-product-option-selected]'
        );

        if (!label) {
          return;
        }

        const checkedInput = group.querySelector(
          '[data-product-option]:checked'
        );

        const select = group.querySelector(
          'select[data-product-option]'
        );

        label.textContent =
          checkedInput?.value ||
          select?.value ||
          '';
      });
    }

    updateUnavailableState() {
      const unavailableText =
        this.container.dataset.unavailableText ||
        'No disponible';

      this.updateButtonState(
        this.addButton,
        false,
        unavailableText
      );

      this.updateButtonState(
        this.mobileButton,
        false,
        unavailableText
      );

      if (this.availability) {
        this.availability.textContent =
          unavailableText;

        this.availability.classList.remove(
          'is-low'
        );

        this.availability.classList.add(
          'is-sold-out'
        );
      }
    }

    updateBrowserURL(variantId) {
      if (
        !variantId ||
        !window.history?.replaceState
      ) {
        return;
      }

      const url = new URL(window.location.href);

      url.searchParams.set(
        'variant',
        String(variantId)
      );

      window.history.replaceState(
        {},
        '',
        url.toString()
      );
    }

    /* ======================================================
       PRECIOS
       ====================================================== */

    updatePrice() {
      if (!this.variant) {
        return;
      }

      const currentPrice = Number(
        this.variant.price || 0
      );

      const compareAtPrice = Number(
        this.variant.compare_at_price || 0
      );

      const isOnSale =
        compareAtPrice > currentPrice;

      const formattedPrice =
        this.formatMoney(currentPrice);

      if (this.price) {
        this.price.textContent =
          formattedPrice;

        this.price.classList.toggle(
          'is-sale',
          isOnSale
        );
      }

      if (this.mobilePrice) {
        this.mobilePrice.textContent =
          formattedPrice;
      }

      if (this.comparePrice) {
        this.comparePrice.textContent =
          isOnSale
            ? this.formatMoney(compareAtPrice)
            : '';

        this.comparePrice.hidden =
          !isOnSale;
      }

      const saleBadge =
        this.container.querySelector(
          '[data-product-sale-badge]'
        );

      const savingsWrapper =
        this.container.querySelector(
          '[data-product-savings]'
        );

      const savingsValue =
        this.container.querySelector(
          '[data-product-savings-value]'
        );

      if (isOnSale) {
        const savedAmount =
          compareAtPrice - currentPrice;

        const discountPercentage =
          Math.round(
            (savedAmount / compareAtPrice) *
              100
          );

        if (saleBadge) {
          saleBadge.hidden = false;
          saleBadge.textContent =
            `-${discountPercentage}%`;
        }

        if (savingsWrapper) {
          savingsWrapper.hidden = false;
        }

        if (savingsValue) {
          savingsValue.textContent =
            `Ahorras ${this.formatMoney(
              savedAmount
            )}`;
        }
      } else {
        if (saleBadge) {
          saleBadge.hidden = true;
          saleBadge.textContent = '';
        }

        if (savingsWrapper) {
          savingsWrapper.hidden = true;
        }

        if (savingsValue) {
          savingsValue.textContent = '';
        }
      }
    }

    /**
     * Shopify entrega los precios en centavos.
     * 229000 se convierte en $2.290.
     */
    formatMoney(cents) {
      const numericCents = Number(cents || 0);

      if (!Number.isFinite(numericCents)) {
        return '$0';
      }

      const pesos = Math.round(
        numericCents / 100
      );

      const formattedNumber =
        new Intl.NumberFormat('es-CL', {
          style: 'decimal',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(pesos);

      return `$${formattedNumber}`;
    }

    /* ======================================================
       INVENTARIO Y SKU
       ====================================================== */

    updateAvailability() {
      if (
        !this.variant ||
        !this.availability
      ) {
        return;
      }

      const available = Boolean(
        this.variant.available
      );

      const inventoryQuantity = Number(
        this.variant.inventory_quantity || 0
      );

      const inventoryManagement =
        this.variant.inventory_management ||
        '';

      const inventoryPolicy =
        this.variant.inventory_policy ||
        'deny';

      const lowStockThreshold = Number(
        this.container.dataset
          .lowStockThreshold || 5
      );

      this.availability.classList.remove(
        'is-low',
        'is-sold-out'
      );

      if (!available) {
        this.availability.classList.add(
          'is-sold-out'
        );

        this.availability.textContent =
          this.container.dataset
            .soldOutText || 'Agotado';

        return;
      }

      if (
        inventoryManagement === 'shopify' &&
        inventoryQuantity > 0 &&
        inventoryQuantity <=
          lowStockThreshold
      ) {
        this.availability.classList.add(
          'is-low'
        );

        this.availability.textContent =
          inventoryQuantity === 1
            ? 'Última unidad disponible'
            : `Quedan ${inventoryQuantity} unidades`;

        return;
      }

      if (
        inventoryManagement === 'shopify' &&
        inventoryQuantity <= 0 &&
        inventoryPolicy === 'continue'
      ) {
        this.availability.textContent =
          this.container.dataset
            .preorderText ||
          'Disponible para encargo';

        return;
      }

      this.availability.textContent =
        this.container.dataset
          .availableText || 'Disponible';
    }

    updateSKU() {
      if (
        !this.sku ||
        !this.variant
      ) {
        return;
      }

      const sku = this.variant.sku || '';

      this.sku.textContent = sku;

      const skuWrapper =
        this.sku.closest(
          '[data-product-sku-wrapper]'
        );

      if (skuWrapper) {
        skuWrapper.hidden =
          sku.length === 0;
      }
    }

    /* ======================================================
       IMÁGENES
       ====================================================== */

    updateImage() {
      if (!this.variant) {
        return;
      }

      const featuredMedia =
        this.variant.featured_media ||
        this.variant.featured_image ||
        null;

      if (!featuredMedia) {
        return;
      }

      const mediaId = Number(
        featuredMedia.id ||
          featuredMedia.media_id ||
          0
      );

      if (mediaId) {
        const matchingThumb =
          this.thumbs.find(
            (button) =>
              Number(
                button.dataset.mediaId
              ) === mediaId
          );

        if (matchingThumb) {
          this.changeImage(
            matchingThumb,
            false
          );

          return;
        }
      }

      const image =
        featuredMedia.preview_image ||
        featuredMedia;

      const source =
        image?.src ||
        image?.url ||
        '';

      if (
        !source ||
        !this.mainImage
      ) {
        return;
      }

      this.mainImage.src = source;

      if (image.srcset) {
        this.mainImage.srcset =
          image.srcset;
      } else {
        this.mainImage.removeAttribute(
          'srcset'
        );
      }

      if (image.alt) {
        this.mainImage.alt = image.alt;
      }

      if (image.width) {
        this.mainImage.width =
          image.width;
      }

      if (image.height) {
        this.mainImage.height =
          image.height;
      }

      if (this.imageLink) {
        this.imageLink.href = source;
      }
    }

    changeImage(
      button,
      focusImage = true
    ) {
      if (
        !button ||
        !this.mainImage
      ) {
        return;
      }

      const source =
        button.dataset.image ||
        button.dataset.src ||
        '';

      const sourceSet =
        button.dataset.srcset ||
        '';

      const alt =
        button.dataset.alt ||
        this.mainImage.alt ||
        '';

      const width = Number(
        button.dataset.width || 0
      );

      const height = Number(
        button.dataset.height || 0
      );

      if (!source) {
        return;
      }

      this.mainImage.src = source;

      if (sourceSet) {
        this.mainImage.srcset =
          sourceSet;
      } else {
        this.mainImage.removeAttribute(
          'srcset'
        );
      }

      this.mainImage.alt = alt;

      if (width) {
        this.mainImage.width = width;
      }

      if (height) {
        this.mainImage.height = height;
      }

      if (this.imageLink) {
        this.imageLink.href =
          button.dataset.fullImage ||
          source;
      }

      this.thumbs.forEach((thumb) => {
        const isActive =
          thumb === button;

        thumb.classList.toggle(
          'is-active',
          isActive
        );

        thumb.setAttribute(
          'aria-current',
          isActive
            ? 'true'
            : 'false'
        );
      });

      if (focusImage) {
        const mainMedia =
          this.mainImage.closest(
            '[data-product-main-media]'
          );

        mainMedia?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }

    /* ======================================================
       BOTONES Y CANTIDAD
       ====================================================== */

    updateButtons() {
      if (!this.variant) {
        return;
      }

      const available = Boolean(
        this.variant.available
      );

      const addText =
        this.container.dataset.addText ||
        'Agregar al carrito';

      const soldOutText =
        this.container.dataset
          .soldOutText || 'Agotado';

      const label = available
        ? addText
        : soldOutText;

      this.updateButtonState(
        this.addButton,
        available,
        label
      );

      this.updateButtonState(
        this.mobileButton,
        available,
        label
      );

      this.container
        .querySelectorAll(
          'input[name="id"], [data-product-variant-id]'
        )
        .forEach((input) => {
          input.value = String(
            this.variant.id
          );

          input.disabled = !available;
        });
    }

    updateButtonState(
      button,
      available,
      label
    ) {
      if (!button) {
        return;
      }

      button.disabled = !available;

      button.classList.toggle(
        'is-sold-out',
        !available
      );

      const textElement =
        button.querySelector(
          '[data-product-button-text]'
        );

      if (textElement) {
        textElement.textContent = label;
      } else {
        button.textContent = label;
      }

      button.setAttribute(
        'aria-disabled',
        available
          ? 'false'
          : 'true'
      );
    }

    updateQuantityRule() {
      if (
        !this.quantityInput ||
        !this.variant
      ) {
        return;
      }

      const quantityRule =
        this.variant.quantity_rule || {};

      const minimum = Number(
        quantityRule.min || 1
      );

      const increment = Number(
        quantityRule.increment || 1
      );

      const maximum =
        quantityRule.max !== null &&
        quantityRule.max !== undefined &&
        quantityRule.max !== ''
          ? Number(quantityRule.max)
          : null;

      this.quantityInput.min =
        String(minimum);

      this.quantityInput.step =
        String(increment);

      if (
        maximum !== null &&
        Number.isFinite(maximum)
      ) {
        this.quantityInput.max =
          String(maximum);
      } else {
        this.quantityInput.removeAttribute(
          'max'
        );
      }

      this.normalizeQuantity();
      this.updateQuantityButtons();
    }

    changeQuantity(direction) {
      if (!this.quantityInput) {
        return;
      }

      const minimum = Number(
        this.quantityInput.min ||
          this.quantityInput.dataset.min ||
          1
      );

      const maximumValue =
        this.quantityInput.max ||
        this.quantityInput.dataset.max;

      const maximum = maximumValue
        ? Number(maximumValue)
        : Infinity;

      const step = Number(
        this.quantityInput.step ||
          this.quantityInput.dataset.step ||
          1
      );

      const currentValue = Number(
        this.quantityInput.value ||
          minimum
      );

      let nextValue =
        currentValue +
        direction * step;

      nextValue = Math.max(
        minimum,
        Math.min(maximum, nextValue)
      );

      this.quantityInput.value =
        String(nextValue);

      this.quantityInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    normalizeQuantity() {
      if (!this.quantityInput) {
        return;
      }

      const minimum = Number(
        this.quantityInput.min ||
          this.quantityInput.dataset.min ||
          1
      );

      const maximumValue =
        this.quantityInput.max ||
        this.quantityInput.dataset.max;

      const maximum = maximumValue
        ? Number(maximumValue)
        : Infinity;

      const step = Number(
        this.quantityInput.step ||
          this.quantityInput.dataset.step ||
          1
      );

      let value = Number(
        this.quantityInput.value
      );

      if (!Number.isFinite(value)) {
        value = minimum;
      }

      value = Math.max(
        minimum,
        Math.min(maximum, value)
      );

      if (step > 1) {
        value =
          minimum +
          Math.round(
            (value - minimum) / step
          ) *
            step;

        value = Math.max(
          minimum,
          Math.min(maximum, value)
        );
      }

      this.quantityInput.value =
        String(value);
    }

    updateQuantityButtons() {
      if (!this.quantityInput) {
        return;
      }

      const value = Number(
        this.quantityInput.value || 1
      );

      const minimum = Number(
        this.quantityInput.min ||
          this.quantityInput.dataset.min ||
          1
      );

      const maximumValue =
        this.quantityInput.max ||
        this.quantityInput.dataset.max;

      const maximum = maximumValue
        ? Number(maximumValue)
        : Infinity;

      this.container
        .querySelectorAll(
          '[data-quantity-minus]'
        )
        .forEach((button) => {
          button.disabled =
            value <= minimum;
        });

      this.container
        .querySelectorAll(
          '[data-quantity-plus]'
        )
        .forEach((button) => {
          button.disabled =
            value >= maximum;
        });
    }

    /* ======================================================
       AGREGAR AL CARRITO
       ====================================================== */

    async addToCart(event) {
      event?.preventDefault();

      if (
        this.isSubmitting ||
        !this.variant ||
        !this.variant.available
      ) {
        return;
      }

      const quantity = Math.max(
        1,
        Number(
          this.quantityInput?.value || 1
        )
      );

      const buttons = [
        this.addButton,
        this.mobileButton
      ].filter(Boolean);

      this.isSubmitting = true;
      this.setLoadingState(buttons, true);
      this.hideFormError();

      try {
        const formData =
          new FormData();

        formData.append(
          'id',
          String(this.variant.id)
        );

        formData.append(
          'quantity',
          String(quantity)
        );

        const cartDrawer =
          document.querySelector(
            'cart-drawer'
          );

        const cartNotification =
          document.querySelector(
            'cart-notification'
          );

        const cartComponent =
          cartDrawer ||
          cartNotification;

        if (
          cartComponent &&
          typeof cartComponent
            .getSectionsToRender ===
            'function'
        ) {
          const sections =
            cartComponent
              .getSectionsToRender()
              .map(
                (section) =>
                  section.id
              );

          if (sections.length > 0) {
            formData.append(
              'sections',
              sections.join(',')
            );

            formData.append(
              'sections_url',
              window.location.pathname
            );
          }
        }

        const root =
          window.Shopify?.routes?.root ||
          '/';

        const response = await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              Accept:
                'application/json',
              'X-Requested-With':
                'XMLHttpRequest'
            },
            body: formData
          }
        );

        const result =
          await response.json();

        if (
          !response.ok ||
          result.status
        ) {
          throw new Error(
            result.description ||
              result.message ||
              'No pudimos agregar el producto al carrito.'
          );
        }

        await this.handleCartSuccess(
          result,
          cartComponent,
          quantity
        );
      } catch (error) {
        console.error(
          'Chemarry Product:',
          error
        );

        this.showFormError(
          error.message ||
            'Ocurrió un error al agregar el producto.'
        );
      } finally {
        this.isSubmitting = false;
        this.setLoadingState(
          buttons,
          false
        );
      }
    }

    async handleCartSuccess(
      result,
      cartComponent,
      quantity
    ) {
      const successMessage =
        this.container.dataset
          .successMessage ||
        'Producto agregado al carrito.';

      if (
        cartComponent &&
        typeof cartComponent
          .renderContents ===
          'function' &&
        result.sections
      ) {
        cartComponent.renderContents(
          result
        );
      } else {
        await this.refreshCartComponents();
      }

      this.showToast(
        successMessage,
        false
      );

      document.dispatchEvent(
        new CustomEvent(
          'chemarry:product-added',
          {
            bubbles: true,
            detail: {
              product:
                this.productJSON,
              variant: this.variant,
              quantity,
              response: result
            }
          }
        )
      );

      document.dispatchEvent(
        new CustomEvent(
          'cart:refresh',
          {
            bubbles: true,
            detail: {
              source:
                'chemarry-product',
              productVariantId:
                this.variant.id
            }
          }
        )
      );

      this.openCartInterface();
    }

    async refreshCartComponents() {
      const cartDrawer =
        document.querySelector(
          'cart-drawer'
        );

      const cartNotification =
        document.querySelector(
          'cart-notification'
        );

      const cartComponent =
        cartDrawer ||
        cartNotification;

      if (
        !cartComponent ||
        typeof cartComponent
          .getSectionsToRender !==
          'function'
      ) {
        return;
      }

      try {
        const sections =
          cartComponent
            .getSectionsToRender()
            .map(
              (section) =>
                section.id
            )
            .join(',');

        if (!sections) {
          return;
        }

        const root =
          window.Shopify?.routes?.root ||
          '/';

        const response = await fetch(
          `${root}?sections=${encodeURIComponent(
            sections
          )}`
        );

        if (!response.ok) {
          return;
        }

        const sectionData =
          await response.json();

        if (
          typeof cartComponent
            .renderContents ===
          'function'
        ) {
          cartComponent.renderContents({
            sections: sectionData
          });
        }
      } catch (error) {
        console.warn(
          'Chemarry Product: no fue posible actualizar visualmente el carrito.',
          error
        );
      }
    }

    openCartInterface() {
      const cartDrawer =
        document.querySelector(
          'cart-drawer'
        );

      const cartNotification =
        document.querySelector(
          'cart-notification'
        );

      window.setTimeout(() => {
        if (
          cartDrawer &&
          typeof cartDrawer.open ===
            'function'
        ) {
          cartDrawer.open();
          return;
        }

        if (
          cartNotification &&
          typeof cartNotification.open ===
            'function'
        ) {
          cartNotification.open();
        }
      }, 120);
    }

    setLoadingState(
      buttons,
      loading
    ) {
      buttons.forEach((button) => {
        if (!button) {
          return;
        }

        button.classList.toggle(
          'is-loading',
          loading
        );

        button.setAttribute(
          'aria-busy',
          loading
            ? 'true'
            : 'false'
        );

        button.disabled =
          loading ||
          !this.variant?.available;
      });
    }

    /* ======================================================
       MENSAJES
       ====================================================== */

    showFormError(message) {
      const errorWrapper =
        this.container.querySelector(
          '[data-product-form-error]'
        );

      if (!errorWrapper) {
        this.showToast(message, true);
        return;
      }

      const errorText =
        errorWrapper.querySelector(
          '[data-product-form-error-text]'
        );

      if (errorText) {
        errorText.textContent =
          message;
      } else {
        errorWrapper.textContent =
          message;
      }

      errorWrapper.hidden = false;

      errorWrapper.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }

    hideFormError() {
      const errorWrapper =
        this.container.querySelector(
          '[data-product-form-error]'
        );

      if (!errorWrapper) {
        return;
      }

      errorWrapper.hidden = true;

      const errorText =
        errorWrapper.querySelector(
          '[data-product-form-error-text]'
        );

      if (errorText) {
        errorText.textContent = '';
      }
    }

    showToast(
      message,
      isError = false
    ) {
      let toast =
        this.container.querySelector(
          '[data-product-toast]'
        );

      if (!toast) {
        toast =
          document.createElement(
            'div'
          );

        toast.className =
          'chemarry-product__toast';

        toast.setAttribute(
          'data-product-toast',
          ''
        );

        toast.setAttribute(
          'role',
          'status'
        );

        toast.setAttribute(
          'aria-live',
          'polite'
        );

        toast.setAttribute(
          'aria-atomic',
          'true'
        );

        document.body.appendChild(
          toast
        );
      }

      toast.textContent = message;

      toast.classList.toggle(
        'is-error',
        isError
      );

      toast.classList.add(
        'is-visible'
      );

      window.clearTimeout(
        this.toastTimeout
      );

      this.toastTimeout =
        window.setTimeout(() => {
          toast.classList.remove(
            'is-visible'
          );
        }, 3200);
    }

    /* ======================================================
       BARRA MÓVIL
       ====================================================== */

    initStickyBar() {
      if (
        !this.stickyBar ||
        !this.addButton
      ) {
        return;
      }

      if (
        !(
          'IntersectionObserver' in
          window
        )
      ) {
        this.stickyBar.classList.add(
          'is-visible'
        );

        return;
      }

      this.stickyObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach(
              (entry) => {
                const shouldShow =
                  !entry.isIntersecting &&
                  window.scrollY > 250;

                this.stickyBar.classList.toggle(
                  'is-visible',
                  shouldShow
                );
              }
            );
          },
          {
            threshold: 0.12
          }
        );

      this.stickyObserver.observe(
        this.addButton
      );
    }

    destroy() {
      if (this.stickyObserver) {
        this.stickyObserver.disconnect();
      }

      window.clearTimeout(
        this.toastTimeout
      );
    }
  }

  /* ========================================================
     INICIALIZACIÓN
     ======================================================== */

  const initializeChemarryProducts = (
    root = document
  ) => {
    const containers = [
      ...root.querySelectorAll(
        '[data-chemarry-product]'
      )
    ];

    containers.forEach(
      (container) => {
        if (
          container.dataset
            .chemarryProductInitialized ===
          'true'
        ) {
          return;
        }

        container.dataset
          .chemarryProductInitialized =
          'true';

        try {
          container.chemarryProduct =
            new ChemarryProduct(
              container
            );
        } catch (error) {
          console.error(
            'No fue posible iniciar la página de producto Chemarry.',
            error
          );

          container.dataset
            .chemarryProductInitialized =
            'false';
        }
      }
    );
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeChemarryProducts();
      }
    );
  } else {
    initializeChemarryProducts();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeChemarryProducts(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const containers = [
        ...event.target.querySelectorAll(
          '[data-chemarry-product]'
        )
      ];

      containers.forEach(
        (container) => {
          container.chemarryProduct?.destroy();
        }
      );
    }
  );

  window.ChemarryProduct =
    ChemarryProduct;
})();